import { defineStore } from 'pinia'
import {
  displayErrorMessage,
  fetchApi,
  fetchCartData,
  fetchHotelCities as fetchHotelCitiesQuery,
  fetchHotelDefaults as fetchHotelDefaultsQuery,
  fetchPackageSummary as fetchPackageSummaryQuery,
  getFetchApiErrorStatus,
  type HotelSearchCriteria,
  type HotelSearchData,
  isFetchApiError,
  mapCartData,
  removeTravelAddOn as removeTravelAddOnQuery,
  searchHotels as searchHotelsQuery,
  searchPackageHotels as searchPackageHotelsQuery,
  selectHotel as selectHotelQuery,
  type CartData,
  type HotelCity,
  type PackageSummaryData,
} from '~/queries'
import { stringSchema } from '~/schemas'
import { createPendingRequest, type TicketProtectionResult } from '~/composables/useTicketProtectionSelection'

// The checkout's "How did you hear about us?" answer, kept per tab so Back to the
// cart and Continue again doesn't lose it (BOAT-1986). Dropped with the cart.
export const CHECKOUT_ATTRIBUTION_STORAGE_KEY = 'checkoutAttribution'

interface State {
  data: CartData | null
  hotelCities: HotelCity[] | null
  /** A Ticket Protection change is on its way to the server. */
  ticketProtectionSaving: boolean
}

/**
 * De-duplicates concurrent catalogue fetches: several components can ask for
 * the city list in the same tick and only one request goes out.
 */
let hotelCitiesRequest: Promise<HotelCity[]> | null = null

/** The Ticket Protection change in flight, so checkout can wait for it. */
const ticketProtectionRequests = createPendingRequest<TicketProtectionResult>()

const cartCookieOptions = {
  maxAge: 2592000,
}

const useCartIdCookie = () => useCookie<string | null>('cartId', cartCookieOptions)
type CartIdCookie = ReturnType<typeof useCartIdCookie>

export const useCartStore = defineStore('cart', {
  state: (): State => ({
    data: null,
    hotelCities: null,
    ticketProtectionSaving: false,
  }),
  getters: {
    hasExpiredItems: (state) => {
      if (!state.data) return false

      const now = new Date()

      return state.data.items.some(
        (item) => item.expiresAt && new Date(item.expiresAt * 1000) < now,
      )
    },
  },
  actions: {
    clear(cartId: CartIdCookie) {
      cartId.value = null
      this.data = null
      try {
        sessionStorage.removeItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY)
      } catch {
        // Server-side or storage blocked — nothing stored to drop.
      }
    },
    clearIfMissing(error: unknown, cartId: CartIdCookie) {
      if (!isFetchApiError(error) || getFetchApiErrorStatus(error) !== 404) {
        return false
      }

      this.clear(cartId)

      return true
    },
    async fetch() {
      const cartId = useCartIdCookie()

      if (!cartId.value) {
        this.data = null

        return null
      }

      try {
        const data = await fetchCartData({ id: cartId.value })

        this.data = data

        return data
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          return null
        }

        throw createError({
          statusCode: 500,
          statusMessage: 'Error fetching cart data',
        })
      }
    },
    async removeCart() {
      this.clear(useCartIdCookie())
    },
    async addToCart({
      productId,
      ticketCategoryId,
      performanceId,
      dateTime,
      variations,
      giftCard,
      openHotels,
      isRetry,
    }: {
      productId: string
      ticketCategoryId?: string
      performanceId?: string
      dateTime?: string
      variations?: any[]
      giftCard?: {
        value: number
        recipient_name: string
        recipient_email?: string
        message?: string
      }
      // Fixtures sold as a package skip the cart review and go straight to the
      // hotel step, which is where the trip is priced as a whole.
      openHotels?: boolean
      isRetry?: boolean
    }) {
      const cartId = useCartIdCookie()
      const modals = useModalsStore()
      const { $analyticsManager } = useNuxtApp()
      const currency = useCurrencyStore()
      const { sendDeviceSignal } = useDeviceSignal()

      try {
        const response: any = await fetchApi(
          cartId.value ? `/cart/${cartId.value}` : `/cart`,
          {
            method: 'POST',
            body: {
              product_id: productId,
              ticket_category_id: ticketCategoryId,
              performance_id: performanceId,
              datetime: dateTime,
              variations,
              gift_card: giftCard,
            },
          },
        )

        cartId.value = stringSchema.min(1).parse(response.id)

        await nextTick(async () => {
          await this.fetch()
          $analyticsManager.trackAddToCart(this.data, this.data?.currency ?? currency.code)
        })

        modals.closeAddToCartModal()

        // Intent, and the first signal most visitors produce after landing
        // (BOAT-1895). No email here, so it records the device and nothing
        // else; the identity arrives with the checkout.
        void sendDeviceSignal('add_to_cart')

        await navigateTo(openHotels ? '/package/hotel' : '/cart')
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          if (isRetry) return

          await nextTick(
            async () =>
              await this.addToCart({
                productId,
                ticketCategoryId,
                performanceId,
                dateTime,
                variations,
                giftCard,
                openHotels,
                isRetry: true,
              }),
          )

          return
        }

        // Reported and done with. Rethrowing sent it on to the app error
        // handler, which replaced the product page with "Oops, something went
        // wrong" and the raw request URL — on top of the toast that had already
        // explained the problem. Every caller here only flips a loading flag,
        // and the success path is finished inside this action, so there is
        // nothing left for them to react to.
        displayErrorMessage(error)
      }
    },
    async removeFromCart(id: string, options?: { showToast?: boolean }) {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCartIdCookie()
      const { t } = $i18n
      const showToast = options?.showToast ?? true

      if (!cartId.value) return

      try {
        const response: any = await fetchApi(`/cart/${cartId.value}/${id}`, {
          method: 'DELETE',
        })

        if (response?.data?.id) {
          this.data = mapCartData(response)
        } else {
          await nextTick(async () => await this.fetch())
        }

        if (showToast) {
          $toast.success(t('feedback.cart.removed'))
        }
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          return
        }

        displayErrorMessage(error)
      }
    },
    async addCoupon({ coupon }: { coupon: string }) {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCartIdCookie()
      const { t } = $i18n

      if (!cartId.value) return

      try {
        await fetchApi(`/cart/${cartId.value}/apply-discount`, {
          method: 'POST',
          body: { code: coupon },
        })

        await nextTick(async () => await this.fetch())

        $toast.success(t('feedback.coupon.added'))
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          return
        }

        displayErrorMessage(error)
      }
    },
    async applyGiftCards({
      giftCards,
    }: {
      giftCards: { code: string; amount?: number }[]
    }) {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCartIdCookie()
      const { t } = $i18n

      if (!cartId.value) return

      try {
        const response: any = await fetchApi(`/cart/${cartId.value}/gift-cards`, {
          method: 'POST',
          body: {
            gift_cards: giftCards,
          },
        })

        this.data = mapCartData(response)
        $toast.success(t('feedback.gift-card.applied'))
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          return
        }

        displayErrorMessage(error)
        throw error
      }
    },
    /**
     * Opt in to / out of Ticket Protection (BOAT-1975). Never throws: the
     * result says whether the cart now has the requested selection. A gone
     * cart is cleared and reported as `cartMissing` (the checkout page is
     * `v-if="cart.data"`, so the page decides where to send the customer).
     * Any other failure is shown, and the cart is re-read so it shows what
     * the server actually has. If that re-read finds the cart gone, that is
     * reported as `cartMissing` too, with the read's 404 as the error.
     */
    setTicketProtection(selected: boolean): Promise<TicketProtectionResult> {
      return ticketProtectionRequests.run(async () => {
        const cartId = useCartIdCookie()
        const id = cartId.value

        if (!id) return { ok: false, cartMissing: false, error: null }

        this.ticketProtectionSaving = true
        try {
          const response: any = await fetchApi(`/cart/${id}/ticket-protection`, {
            method: 'PUT',
            body: { selected },
          })

          this.data = mapCartData(response)

          return { ok: true }
        } catch (error) {
          if (this.clearIfMissing(error, cartId)) {
            return { ok: false, cartMissing: true, error }
          }

          // Not `this.fetch()`: it swallows a 404, and the page needs that
          // 404 to send the customer away from a blank checkout.
          try {
            this.data = await fetchCartData({ id })
          } catch (readError) {
            if (this.clearIfMissing(readError, cartId)) {
              return { ok: false, cartMissing: true, error: readError }
            }
            // Keep what we have; the PUT's error is shown below.
          }

          displayErrorMessage(error)

          return { ok: false, cartMissing: false, error }
        } finally {
          this.ticketProtectionSaving = false
        }
      })
    },
    /** Resolves once no Ticket Protection change is in flight. */
    ticketProtectionSettled(): Promise<void> {
      return ticketProtectionRequests.settled()
    },
    async removeAppliedGiftCard(code: string) {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCartIdCookie()
      const { t } = $i18n

      if (!cartId.value) return

      try {
        const response: any = await fetchApi(
          `/cart/${cartId.value}/gift-cards/${encodeURIComponent(code)}`,
          {
            method: 'DELETE',
          },
        )

        this.data = mapCartData(response)
        $toast.success(t('feedback.gift-card.removed'))
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          return
        }

        displayErrorMessage(error)
        throw error
      }
    },
    async removeCoupon() {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCartIdCookie()
      const { t } = $i18n

      if (!cartId.value) return

      try {
        await fetchApi(`/cart/${cartId.value}/remove-discount`, {
          method: 'DELETE',
        })

        await nextTick(async () => await this.fetch())

        $toast.success(t('feedback.coupon.removed'))
      } catch (error) {
        if (this.clearIfMissing(error, cartId)) {
          return
        }

        displayErrorMessage(error)
      }
    },
    async fetchHotelDefaults(): Promise<HotelSearchCriteria | null> {
      const cartId = useCookie('cartId', cartCookieOptions)

      if (!cartId.value) return null

      return await fetchHotelDefaultsQuery({ cartId: cartId.value })
    },
    /**
     * The provider city catalogue, fetched at most once per session. Returns
     * the cached list on every later call.
     */
    async fetchHotelCities(): Promise<HotelCity[]> {
      if (this.hotelCities) return this.hotelCities
      if (hotelCitiesRequest) return hotelCitiesRequest

      const cartId = useCartIdCookie()

      if (!cartId.value) return []

      hotelCitiesRequest = fetchHotelCitiesQuery({ cartId: cartId.value })
        .then((response) => {
          this.hotelCities = response.items

          return response.items
        })
        .catch(() => [])
        .finally(() => {
          hotelCitiesRequest = null
        })

      return hotelCitiesRequest
    },
    async searchHotels(
      search: Parameters<typeof searchHotelsQuery>[0]['search'],
    ): Promise<HotelSearchData | null> {
      const cartId = useCookie('cartId', cartCookieOptions)

      if (!cartId.value) return null

      try {
        return await searchHotelsQuery({ cartId: cartId.value, search })
      } catch (error) {
        if (this.clearIfMissing(error)) {
          return null
        }

        displayErrorMessage(error)
        throw error
      }
    },
    /**
     * The package step's search: the same hotels, each priced as the whole trip
     * it would complete. Kept separate from searchHotels so the cart add-on flow
     * does not carry package data it never shows.
     */
    async searchPackageHotels(
      search: Parameters<typeof searchPackageHotelsQuery>[0]['search'],
    ): Promise<HotelSearchData | null> {
      const cartId = useCookie('cartId', cartCookieOptions)

      if (!cartId.value) return null

      try {
        return await searchPackageHotelsQuery({ cartId: cartId.value, search })
      } catch (error) {
        if (this.clearIfMissing(error)) {
          return null
        }

        displayErrorMessage(error)
        throw error
      }
    },
    async fetchPackageSummary(): Promise<PackageSummaryData | null> {
      const cartId = useCookie('cartId', cartCookieOptions)

      if (!cartId.value) return null

      try {
        return await fetchPackageSummaryQuery({ cartId: cartId.value })
      } catch (error) {
        if (this.clearIfMissing(error)) {
          return null
        }

        displayErrorMessage(error)
        throw error
      }
    },
    async selectHotel(payload: Record<string, unknown>) {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCookie('cartId', cartCookieOptions)
      const { t } = $i18n

      if (!cartId.value) return null

      try {
        this.data = await selectHotelQuery({ cartId: cartId.value, payload })
        $toast.success(t('feedback.cart.hotel-added'))

        return this.data
      } catch (error) {
        if (this.clearIfMissing(error)) {
          return null
        }

        displayErrorMessage(error)
        throw error
      }
    },
    async removeTravelAddOn(addOnId: string) {
      const { $toast, $i18n } = useNuxtApp()
      const cartId = useCookie('cartId', cartCookieOptions)
      const { t } = $i18n

      if (!cartId.value) return

      try {
        await removeTravelAddOnQuery({ cartId: cartId.value, addOnId })
        await nextTick(async () => await this.fetch())
        $toast.success(t('feedback.cart.hotel-removed'))
      } catch (error) {
        if (this.clearIfMissing(error)) {
          return
        }

        displayErrorMessage(error)
      }
    },
    refreshGetters() {
      this.data = this.data ? { ...this.data } : null
    },
  },
})
